import _ from 'lodash';
import defaultThemeData from 'components/sharpr/theme/defaultThemeData';

let loaded = {};

let bubbleCss = {};

/**
 * Handle inserting scripts and stylesheets and manage document css classes
 */
export class AssetManager {
	/**
	 * Remove a CSS class from document
	 * @param {String} className  The class name to remove. May contain an asterisk wildcard
	 * @returns {AssetManager}
	 */
	static removeDocClass(className) {
		let doc = document.documentElement;
		if (_.includes(className, '*')) {
			let regex = new RegExp('\\b' + className.replace(/\*/g, '[^ ]*') + '\\b');
			doc.className = doc.className.replace(regex, '');
		} else {
			angular.element(doc).removeClass(className);
		}
		return this;
	}

	/**
	 * Add a CSS class from document
	 * @param {String} className  The class name to dadd
	 * @returns {AssetManager}
	 */
	static addDocClass(className) {
		angular.element(document.documentElement).addClass(className);
		return this;
	}

	/**
	 * Insert a script tag with the given src attribute
	 * @param {String|String[]} src  A URL or Array or URLs of scripts to load
	 * @param {Object} [props]  Other properties to add to the script tag such as {asyc:true}
	 * @returns {Promise}  Promise resolves when script has loaded
	 */
	static script(src, props = {}) {
		if (Array.isArray(src)) {
			const promises = [];
			src.forEach(s => promises.push(AssetManager.script(s, props)));
			return Promise.all(promises);
		}
		const absUrl = this.handleUrl(src);
		// read or write cache
		if (loaded[absUrl] && loaded[absUrl].element.parentNode) {
			return loaded[absUrl].promise;
		}
		// create and append script tag
		props.src = absUrl;
		const script = this.createAndAppendElement('script', props);
		// return a promise that will be resolved onload or rejected onerror
		loaded[absUrl] = {
			element: script,
			promise: new Promise((resolve, reject) => {
				script.onload = () => resolve(script);
				script.onerror = () => reject(script);
			}),
		};
		return loaded[absUrl].promise;
	}

	/**
	 * Fetch css at the given href and insert a <style> tag with its contents
	 * @param {String} href  The URL to fetch
	 * @returns {Promise}  Resolves when the css file has been loaded
	 */
	static css(href) {
		if (Array.isArray(href)) {
			const promises = [];
			href.forEach(h => promises.push(AssetManager.css(h)));
			return Promise.all(promises);
		}
		const absUrl = this.handleUrl(href);
		if (loaded[absUrl] && loaded[absUrl].element.parentNode) {
			return loaded[absUrl].promise;
		}
		const link = this.createAndAppendElement('link', {
			type: 'text/css',
			rel: 'stylesheet',
			href: absUrl,
		});
		loaded[absUrl] = {
			element: link,
			promise: new Promise((resolve, reject) => {
				link.onload = () => resolve(link);
				link.onerror = () => reject(link);
			}),
		};
		return loaded[absUrl].promise;
	}

	/**
	 * Insert a style tag with the given textContent
	 * @param {String} css  The css to insert
	 * @return {Element} The style element that was inserted
	 */
	static style(css) {
		// string to inject into style tag
		let style = AssetManager.createAndAppendElement('style', {
			textContent: css,
		});
		return style;
	}

	/**
	 * Remove an element or one or more DOM elements by selector
	 * @param {String|Element} elementOrSelector  Any DOM Element or CSS selector
	 * @returns {AssetManager}
	 */
	static removeElement(elementOrSelector) {
		let elements;
		if (typeof elementOrSelector === 'string') {
			// run the selector
			elements = document.querySelectorAll(elementOrSelector);
		} else {
			// assume it is an Element
			elements = [elementOrSelector];
		}
		_.forEach(elements, node => {
			// remove node if applicable
			if (node && node.parentNode && node.parentNode.removeChild) {
				node.parentNode.removeChild(node);
			}
		});
		return this;
	}

	/**
	 * Remove a <script> or <link> that was loaded
	 * @param {String} url  The URL used earlier to add the element
	 * @returns {Boolean}  True on success, false on failure
	 */
	static remove(url) {
		try {
			let element = loaded[url].element;
			element.parentNode.removeChild(element);
			return true;
		} catch (e) {
			return false;
		}
	}

	/**
	 * Process a URL and make it absolute
	 * @param {String} url  The URL to process
	 * @returns {String}
	 */
	static handleUrl(url) {
		const proto = document.location.protocol;
		if (url.slice(0, 2) == '//') {
			url = proto + url;
		} else if (url.slice(0, 1) == '/') {
			url = proto + '//' + document.location.hostname + url;
		} else if (url.slice(0, 3) == '://') {
			url = proto.slice(0, -1) + url;
		} else if (
			// make sure use of SSL matches protocol
			(url.match(/^https:/) && proto == 'http:') ||
			(url.match(/^http:/) && proto == 'https:')
		) {
			url = proto + url.replace(/^https?:/, '');
		}
		url += (url.indexOf('?') == -1 ? '?' : '&') + window.BUILD;
		return url;
	}

	/**
	 * Create an element of the given tag name with the given properties and append it to document.head
	 * @param {String} tagName
	 * @param {Object} [props={}]  key-value pairs corresponding to element attributes. To apply a class/classes, send a prop with key `classNames`. `classNames` is an array of strings with each class you want to include.
	 * @param {String} appendTo  a CSS selector. That element will become the parent of the new element.
	 * @returns {Element}
	 */
	static createAndAppendElement(tagName, props = {}, appendTo) {
		let element = document.createElement(tagName);
		_.extend(element, props);
		if ('classNames' in props) {
			element.classList.add(...props['classNames']);
		}
		if (appendTo) {
			document.querySelector(appendTo).appendChild(element);
		} else {
			// depending on when we are called, document.head and document.body may not be defined
			(
				document.head ||
				document.getElementsByTagName('head')[0] ||
				document.body ||
				document.getElementsByTagName('body')[0]
			).appendChild(element);
		}
		return element;
	}

	/**
	 * Construct and insert CSS for a single bubble color
	 * @param {Object} post  An object from `GET /posts/:id` that contains a hex in post.colors.bubble
	 */
	static loadBubbleCss(post) {
		let hex = post && post.colors && post.colors.bubble ? post.colors.bubble : defaultThemeData.bubble;
		if (!bubbleCss[hex]) {
			// css for bubble color of every theme
			bubbleCss[hex] = AssetManager.style(`
				.intel-bubble-${hex.slice(1)} {
					background: ${hex} !important;
				}
				.intel-bubble-${hex.slice(1)}:after {
					border-color: ${hex} transparent transparent transparent !important;
				}
				.intel-bubble-${hex.slice(1)} .faux-checkbox input + span:before {
					color: rgba(255,255,255,0.6);
					background-color: ${hex};
				}
			`);
		}
	}
}
